/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect, useState } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import RentIcon from "../../../assets/icons/provision/toggleable/IconDocumentHouseSignDetailed";
import OwnerIcon from "../../../assets/icons/provision/toggleable/IconHouse";
import _isNaN from "lodash/isNaN";
import MoreInfoIcon from "../../SvgIcon/MoreInfoIcon";
import Button from "@mui/material/Button";
import DialogModal from "../../DialogModal/DialogModal";
import _slice from "lodash/slice";
import _replace from "lodash/replace";
import _round from "lodash/round";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import PropertyLoan from "./PropertyLoan/PropertyLoan";
import { numberFieldDefault, requiredValidator } from "../../../services/validationRules";
import InputAdornment from "@mui/material/InputAdornment";
import { useLocation, useNavigate } from "react-router";
import { useForm } from "../../../provider/Form/FormProvider";
import _toNumber from "lodash/toNumber";
import _cloneDeep from "lodash/cloneDeep";
import { Box, MenuItem, TextField } from "@mui/material";
import Group from "../../Wizard/Group";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import NumberFieldConfigured from "../ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import wizardStyle from "../wizardStyle";
import InfoIconButton from "../../Button/InfoIconButton";

type LivingTypeProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function LivingType({ submitProps, product }: LivingTypeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [fullCosts, setFullCosts] = useState("");
  const { values, handleChange, handleSubmit } = useForm();

  const modalOpen = (modalType: string) => {
    navigate(location.pathname, { state: { rentModalOpen: modalType } });
  };

  const modalClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    const numberOfPropertyLoans = values.numberOfPropertyLoans ? _toNumber(values.numberOfPropertyLoans) : 0;
    if (values.propertyLoans.length === numberOfPropertyLoans) {
      return;
    }
    const emptyPropertyLoan = {
      date: "",
      borrowers: "",
      bank: "",
      remainingTerm: "",
      outstandingDebt: "",
      repayment: "",
    };
    let propertyLoans = _cloneDeep(values.propertyLoans);
    for (let i = propertyLoans.length; i < Math.min(numberOfPropertyLoans, 5); i++) {
      propertyLoans.push(_cloneDeep(emptyPropertyLoan));
    }
    propertyLoans = _slice(propertyLoans, 0, numberOfPropertyLoans);
    handleChange({
      target: {
        type: "other",
        name: "propertyLoans",
        value: propertyLoans,
      },
    });
  }, [values.propertyLoans, values.numberOfPropertyLoans]);

  const { basicRentOrRepayment, heatingCosts, additionalCosts } = values;
  useEffect(() => {
    const fullCostsSum = parseFloat(basicRentOrRepayment) + parseFloat(heatingCosts) + parseFloat(additionalCosts);
    setFullCosts(_isNaN(fullCostsSum) ? "" : _replace(String(_round(fullCostsSum, 2)), ".", ","));
  }, [basicRentOrRepayment, heatingCosts, additionalCosts]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ausgaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"livingType"}
                question={"Wohnen Sie zur Miete oder sind Sie Eigentümer?"}
                modalContent={
                  <p>
                    Wenn Ihnen der Wohnraum nicht gehört und Sie dem Eigentümer Geld oder sonstiges im Gegenzug für die Nutzung des Wohnraumes geben, wählen Sie
                    hier auch "zur Miete" aus.
                  </p>
                }
                options={[
                  {
                    labelText: "Ich wohne zur Miete",
                    labelIcon: <RentIcon />,
                    labelIconActive: <RentIcon active />,
                    value: "rent",
                  },
                  {
                    labelText: "Ich bin Eigentümer",
                    labelIcon: <OwnerIcon />,
                    labelIconActive: <OwnerIcon active />,
                    value: "owner",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.livingType && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Bitte geben Sie genauere Details zum Wohnraum an, in dem Sie allein oder gemeinsam mit anderen Personen wohnen.</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="appartmentSize"
                    label="Größe in Quadratmetern"
                    validators={[requiredValidator("Bitte geben Sie die Größe in Quadratmetern an"), numberFieldDefault]}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="numberRooms"
                    label="Anzahl Zimmer"
                    validators={[requiredValidator("Bitte geben Sie die Anzahl Zimmer an"), numberFieldDefault]}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="numberRoommates"
                    label="Anzahl Mitbewohner"
                    validators={[requiredValidator("Bitte geben Sie die Anzahl Mitbewohner an"), numberFieldDefault]}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <InfoIconButton onClick={() => modalOpen("numberRoommates")} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>
                    {values.livingType === "rent"
                      ? "Bitte machen Sie genauere Angaben zu den Wohnkosten. Alle Angaben können Sie Ihrem Mietvertrag entnehmen"
                      : "Bitte machen Sie genauere Angaben zu den Kosten für Ihren Wohnraum."}
                    {values.livingType === "rent" && (
                      <Button fullWidth={true} variant={"text"} color={"primary"} onClick={() => modalOpen("rent")}>
                        <MoreInfoIcon sx={wizardStyle.leftIcon} /> Weitere Informationen
                      </Button>
                    )}
                  </FormLegend>
                </Grid>
                <NumberFieldConfigured caseData={values} fieldPath={"basicRentOrRepayment"} fieldType={"money"} numberType={"float"} required />

                <NumberFieldConfigured caseData={product} fieldPath={"heatingCosts"} fieldType={"money"} numberType={"float"} required />
                <NumberFieldConfigured caseData={product} fieldPath={"additionalCosts"} fieldType={"money"} numberType={"float"} required />

                <Grid size={12}>
                  <TextField
                    fullWidth={true}
                    label={values.livingType === "rent" ? "Gesamtmiete" : "Gesamtbetrag monatliche Kosten"}
                    margin="normal"
                    value={fullCosts}
                    disabled={true}
                  />
                </Grid>
                <NumberFieldConfigured caseData={product} fieldPath={"costPaidByPetitioner"} numberType={"float"} fieldType={"money"} required />
              </>
            )}
            {values.livingType === "owner" && (
              <>
                <Grid size={12}>
                  <Group
                    type={"radio"}
                    name={"hasPropertyLoans"}
                    question="Zahlen Sie Kredite ab, die Sie für den Bau, Kauf oder den Erhalt der Immobilie aufgenommen haben?"
                    options={[
                      {
                        labelText: "Nein",
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                      {
                        labelText: "Ja",
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
              </>
            )}
            {values.hasPropertyLoans === "yes" && (
              <>
                <Grid size={12}>
                  <ValidatorSelectField
                    label="Wie viele Kredite zahlen Sie ab?"
                    name="numberOfPropertyLoans"
                    validators={[requiredValidator("Bitte geben Sie an wie viele Kredite zahlen Sie abzahlen")]}
                  >
                    <MenuItem value={"1"}>1</MenuItem>
                    <MenuItem value={"2"}>2</MenuItem>
                    <MenuItem value={"3"}>3</MenuItem>
                    <MenuItem value={"4"}>4</MenuItem>
                    <MenuItem value={"5"}>5</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                {values.propertyLoans?.map((propertyLoan: any, index: number) => (
                  <PropertyLoan key={index} index={index} path={`propertyLoans[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
      <DialogModal open={!!(location.state && location.state.rentModalOpen === "rent")} onClose={modalClose} title={"Weitere Informationen"}>
        <p>
          Mietnebenkosten sind – außer den gesondert anzugebenden Heizungskosten – die auf die Mieter umgelegten Betriebskosten (z.B. Grundsteuer,
          Wasserversorgung, Entwässerung, Straßenreinigung, Müllbeseitigung, Schornsteinfeger, Aufzug, Allgemeinstrom, Hausreinigung, Gemeinschaftsantenne
          usw.).
          <strong>Nicht</strong> hierzu gehören jedoch Gas- oder Stromkosten für die eigene Wohnung (soweit es sich nicht um Heizkosten handelt), Telefon oder
          Rundfunkbeiträge. Wenn Sie den Wohnraum mit einer anderen Person teilen, tragen Sie bitte nur die für Sie anfallenden Kosten ein.
        </p>
      </DialogModal>
      <DialogModal open={!!(location.state && location.state.rentModalOpen === "numberRoommates")} onClose={modalClose} title={"Anzahl Mitbewohner"}>
        <p>Geben Sie hier die Anzahl der Personen an, die außer Ihnen noch in dem Wohnraum wohnen. Wenn Sie alleine wohnen, geben Sie hier “0” an.</p>
      </DialogModal>
    </Paper>
  );
}
